import { DataSource } from 'hooks/useHttpClient';

export const defaultPaginatedListValue = { data: [], meta: { perPage: 0, pageCount: 0, totalCount: 0 } };

export enum Entity {
    Generic = 'Generic',
    Login = 'Login',
    Company = 'Company',
    Candidate = 'Candidate',
    Job = 'Job',
    Teams = 'Teams',
    Tasks = 'Tasks',
    Onboarding = 'Onboarding',
    Legacy = 'Legacy',
    Users = 'Users'
}

export enum MappedEntityType {
    // Company
    CreateCompany = 'CreateCompany',
    CompanyNameAvailable = 'CompanyNameAvailable',
    CompanyList = 'CompanyList',
    GetCompany = 'GetCompany',
    UpdateCompany = 'UpdateCompany',
    CompanyCommentList = 'CompanyComment',
    CreateCompanyComment = 'CreateCompanyComment',
    DeleteCompanyComment = 'DeleteCompanyComment',
    CompanyMembersList = 'CompanyMembersList', // Think on a way to get this off of the company logic
    CompanyDocumentsList = 'CompanyDocumentsList', // Think on a way to get this off of the company logic
    GetCompanyOptions = 'GetCompanyOptions', // TEAMS and CREATE JOB
    GetCompanyOptionsStatus = 'GetCompanyOptionsStatus', // TEAMS and CREATE JOB
    // Candidate
    CandidateList = 'CandidateList',
    GetCandidate = 'GetCandidate',
    GenericGetComments = 'GenericGetComments',
    PostCandidate = 'PostCandidate',
    PutCandidate = 'PutCandidate',
    GetCandidateDevice = 'GetCandidateDevice',
    // Job
    JobList = 'JobList',
    GetJob = 'GetJob',
    JobPipeline = 'JobPipeline',
    ApplicationStatus = 'ApplicationStatus',
    GetApplication = 'GetApplication',
    CompanyJobList = 'CompanyJobList',
    PostJob = 'PostJob',
    PutJob = 'PutJob',
    JobsAsOptions = 'JobsAsOptions',
    CreateJobComment = 'CreateJobComment',
    // Teams
    GetTeamByCompany = 'GetTeamByCompany',
    GetFullTeamMember = 'GetFullTeamMember',
    GetMemberDocuments = 'GetMemberDocuments',
    PostMemberFeedback = 'PostMemberFeedback',
    // Tasks
    GetTasks = 'GetTasks',
    GetFastTasks = 'GetFastTasks',
    PostTask = 'PostTask',
    GetSingleTask = 'GetSingleTask',
    PutTask = 'PutTask',
    GetPossibleMembers = 'GetPossibleMembers',
    // Onboarding
    OnboardingList = 'OnboardingList',
    GetOnboarding = 'GetOnboarding',
    GetOnboardingTasks = 'GetOnboardingTasks',
    GetOnboardingDocuments = 'GetOnboardingDocuments',
    PutObnboarding = 'PutObnboarding',
    PutOnboardingTask = 'PutOnboardingTask',
    GetOnboardingQuestionnaire = 'GetOnboardingQuestionnaire',
    PostOnboardingQuestionnaire = 'PostOnboardingQuestionnaire',
    // Users
    UserList = 'UserList',
    GetUser = 'GetUser',
    PutUser = 'PutUser',
    PutMyself = 'PutMyself',
    GetFullMyself = 'GetFullMyself',
    PostCreateNewUser = 'PostCreateNewUser',
    // Login
    LoginResponse = 'LoginResponse',
    ForgotPassword = 'ForgotPassword',
    RecoveryPassword = 'RecoveryPassword',
    ResendInvite = 'ResendInvite',
    ValidateInvite = 'ValidateInvite',
    FinishSignup = 'FinishSignup',
    GoogleResponse = 'GoogleResponse',
    // Common
    GetDocument = 'GetDocument',
    GetMyself = 'GetMyself',
    GetPermissions = 'GetPermissions',
    PostComment = 'PostComment',
    HardwareList = 'HardwareList',
    PostFeedback = 'PostFeedback',
    PutFeedback = 'PutFeedback',
    DeleteFeedback = 'DeleteFeedback',
    UploadDocument = 'UploadDocument',
    UploadBase64Document = 'UploadBase64Document',
    NotificationList = 'NotificationList',
    ChangePassword = 'ChangePassword',
    SignAgreement = 'SignAgreement',
    // Legacy
    LegacyCountry = 'LegacyCountry'
}

export type InternalUrl = {
    // ADD DATA SOURCE HERE
    url?: string;
    method: 'get' | 'post' | 'put' | 'delete' | 'blob' | 'rawPost';
};

export type Url = {
    url: string;
    method: 'get' | 'post' | 'put' | 'delete' | 'blob' | 'rawPost';
};

export type ResolvedPath = {
    entity: Entity;
    basePath: string;
    paths: {
        [key in MappedEntityType]?: InternalUrl;
    };
};

export const resolvedPaths: ResolvedPath[] = [
    {
        entity: Entity.Generic,
        basePath: '/',
        paths: {
            GenericGetComments: { url: '/feedback', method: 'get' },
            PostComment: { method: 'post' },
            GetDocument: { url: '', method: 'blob' },
            GetMyself: { url: '/me', method: 'get' },
            HardwareList: { url: '/hardware', method: 'get' },
            PostFeedback: { url: '/feedback', method: 'post' },
            PutFeedback: { url: '/feedback', method: 'put' },
            DeleteFeedback: { url: '/feedback', method: 'delete' },
            UploadDocument: { url: '', method: 'rawPost' },
            UploadBase64Document: { url: '', method: 'post' },
            GetPermissions: { url: '/auth/profile', method: 'get' },
            NotificationList: { url: '/app/notifications', method: 'get' },
            ChangePassword: { url: '/change-password', method: 'post' },
            SignAgreement: { url: '/sign-agreement', method: 'post' }
        }
    },
    {
        entity: Entity.Login,
        basePath: '/auth/login', // '/auth/login'
        paths: {
            LoginResponse: { method: 'post' },
            ForgotPassword: { method: 'post', url: '/password/email' },
            RecoveryPassword: { method: 'post', url: '/password/reset' },
            FinishSignup: { method: 'post', url: '/signup' },
            ValidateInvite: { method: 'post', url: '/invite-validate' },
            GoogleResponse: { method: 'post', url: '/google/validate' },
            ResendInvite: { method: 'post', url: '/resend-invitation' }
        }
    },
    {
        entity: Entity.Company,
        basePath: '/company',
        paths: {
            CreateCompany: { method: 'post' },
            CompanyNameAvailable: { url: '/company-name-availability', method: 'get' },
            CompanyList: { method: 'get' },
            GetCompany: { method: 'get' },
            CompanyMembersList: { method: 'get' },
            UpdateCompany: { method: 'put' },
            CompanyComment: { url: '/companies', method: 'get' },
            CreateCompanyComment: { url: '/companies', method: 'post' },
            DeleteCompanyComment: { url: '/comments', method: 'delete' },
            CompanyDocumentsList: { url: '/companies', method: 'get' },
            GetCompanyOptions: { method: 'get', url: '/teams/companies' }, // TEAMS
            GetCompanyOptionsStatus: { method: 'get', url: '/companies/raw' } // CREATE JOB
        }
    },
    {
        entity: Entity.Candidate,
        basePath: '/candidates',
        paths: {
            CandidateList: { url: '/candidates/search', method: 'get' },
            GetCandidate: { method: 'get' },
            PostCandidate: { method: 'post' },
            PutCandidate: { method: 'put' },
            GetCandidateDevice: { method: 'get' }
        }
    },
    {
        entity: Entity.Teams,
        basePath: '/teams',
        paths: {
            GetTeamByCompany: { method: 'get' },
            GetFullTeamMember: { method: 'get', url: '/teams/candidates' },
            GetMemberDocuments: { url: '/documents/candidate', method: 'get' },
            PostMemberFeedback: { url: '/teams', method: 'post' }
        }
    },
    {
        entity: Entity.Onboarding,
        basePath: '/onboardings',
        paths: {
            OnboardingList: { method: 'get' },
            GetOnboarding: { method: 'get' },
            GetOnboardingTasks: { method: 'get' },
            PutOnboardingTask: { url: '/onboardings/tasks', method: 'put' },
            GetOnboardingQuestionnaire: { method: 'get' },
            PostOnboardingQuestionnaire: { method: 'post' },
            PutObnboarding: { method: 'put' },
            GetOnboardingDocuments: { method: 'get' }
        }
    },
    {
        entity: Entity.Tasks,
        basePath: '/tasks',
        paths: {
            GetTasks: { method: 'get' },
            GetFastTasks: { method: 'get' },
            GetSingleTask: { method: 'get' },
            PutTask: { method: 'put' },
            PostTask: { method: 'post' },
            GetPossibleMembers: { url: '/paas/possibleMembers', method: 'get' }
        }
    },
    {
        entity: Entity.Job,
        basePath: '/job-requisition',
        paths: {
            JobList: { url: '/job-requisition/search', method: 'get' },
            CompanyJobList: { method: 'get' },
            GetJob: { method: 'get' },
            JobPipeline: { url: '/candidate/stage/job-requisition', method: 'get' },
            ApplicationStatus: { url: '/candidate/stage/job-requisition', method: 'put' },
            GetApplication: { url: '/candidates', method: 'get' },
            PostJob: { method: 'post' },
            PutJob: { method: 'put', url: '/job-requisition' },
            JobsAsOptions: { method: 'get' },
            CreateJobComment: { method: 'post', url: '/job-requisition/comment' }
        }
    },
    {
        entity: Entity.Legacy,
        basePath: '/',
        paths: {
            LegacyCountry: { url: '/countries', method: 'get' }
        }
    },
    {
        entity: Entity.Users,
        basePath: '/users',
        paths: {
            UserList: { url: '/users/search', method: 'get' },
            GetUser: { url: '/admin/users', method: 'get' },
            PutUser: { url: '/admin/users', method: 'put' },
            PutMyself: { method: 'put' },
            GetFullMyself: { url: '/me', method: 'get' },
            PostCreateNewUser: { url: '/auth/account', method: 'post' }
        }
    }
];

export function resolvePath(dataSource: DataSource, entity: Entity, type: MappedEntityType): Url {
    const resolvedEntityPaths = resolvedPaths.find((path) => path.entity === entity);
    if (!resolvedEntityPaths) {
        throw new Error(`Entity not found for ${entity}`);
    }

    const path = resolvedEntityPaths.paths[type];
    if (!path) {
        throw new Error(`Path not found for ${entity} and ${type}`);
    }

    return {
        url: path.url || resolvedEntityPaths.basePath,
        method: path.method
    };
}
